import { useState } from "react";
import {
  FileText,
  Landmark,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";

import { Button } from "../../../ui/button";
import { formatCurrencyFromCents } from "../../../../lib/financials";
import type {
  ManualEntryKind,
  ManualEntryRequest,
  ManualFinancialEntry,
} from "../../../../lib/onboarding-api";
import { InputVerificationCTAs } from "../InputVerificationCTAs";
import { ManualEntryForm } from "./manual-entry-form";

// formatEntryValue renders the per-entry value line ("$2,000/mo", "$50,000",
// "$179,000/yr"). The display contract: integer dollars only, comma grouping,
// frequency suffix when meaningful. formatCurrencyFromCents handles the
// cents → "$X,XXX" formatting consistently with the rest of Profile.
function formatEntryValue(entry: ManualFinancialEntry): string {
  if (entry.entry_kind === "liability" && entry.monthly_payment_cents != null) {
    return `${formatCurrencyFromCents(entry.monthly_payment_cents)}/mo`;
  }
  if (entry.entry_kind === "income" && entry.income_amount_cents != null) {
    const freq = entry.income_frequency === "annual" ? "/yr" : "/mo";
    return `${formatCurrencyFromCents(entry.income_amount_cents)}${freq}`;
  }
  if (entry.entry_kind === "asset" && entry.asset_value_cents != null) {
    return formatCurrencyFromCents(entry.asset_value_cents);
  }
  return "—";
}

// ─── List component ───────────────────────────────────────────────────────────

export interface ManualEntryListProps {
  kind: ManualEntryKind;
  entries: ManualFinancialEntry[];
  isLoading: boolean;
  // Copy varies by row (e.g., "Add income source" vs "Add debt"). Caller
  // owns the label so each row reads naturally in its context.
  addLabel: string;
  emptyMessage: string;
  onCreate: (req: ManualEntryRequest) => Promise<void>;
  onUpdate: (
    id: string,
    req: Omit<ManualEntryRequest, "entry_kind">,
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  // 2026-05-19 finances handoff: inline verification CTAs rendered next
  // to the "+ Add" button. Both must be wired for the pair to render —
  // a half-pair is a misconfigured caller, not a partial UI.
  //
  // bankComingSoon (added 2026-05-28) defers the Connect bank affordance
  // by rendering a non-clickable "Coming soon" pill in its place.
  // onVerifyConnectBank still has to be supplied alongside onVerifyUploadDocs
  // so the pair renders — the value is ignored when bankComingSoon is true.
  onVerifyConnectBank?: () => void;
  onVerifyUploadDocs?: () => void;
  bankComingSoon?: boolean;
}

export function ManualEntryList({
  kind,
  entries,
  isLoading,
  addLabel,
  emptyMessage,
  onCreate,
  onUpdate,
  onDelete,
  onVerifyConnectBank,
  onVerifyUploadDocs,
  bankComingSoon = false,
}: ManualEntryListProps) {
  const [formState, setFormState] = useState<
    | { mode: "closed" }
    | { mode: "add" }
    | { mode: "edit"; entry: ManualFinancialEntry }
  >({ mode: "closed" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const closeForm = () => {
    setFormState({ mode: "closed" });
    setSubmitError(null);
  };

  const handleSubmit = async (req: ManualEntryRequest) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (formState.mode === "edit") {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { entry_kind, ...rest } = req;
        await onUpdate(formState.entry.id, rest);
      } else {
        await onCreate(req);
      }
      closeForm();
    } catch {
      setSubmitError("Failed to save. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } catch {
      // Keep the entry on failure — don't silently lose data.
    } finally {
      setDeletingId(null);
    }
  };
  const openAddForm = () => setFormState({ mode: "add" });
  const verificationActions =
    onVerifyConnectBank && onVerifyUploadDocs
      ? { onConnectBank: onVerifyConnectBank, onUploadDocs: onVerifyUploadDocs }
      : null;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-slot="manual-entry-list">
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ul className="divide-y divide-border-soft">
          {entries.map((entry) => {
            const sourceFamily = entry.source_family ?? entry.source;
            const isDocBacked = sourceFamily === "document";
            const isPlaidBacked = sourceFamily === "plaid";
            const isEditable = entry.editable ?? sourceFamily === "manual";
            const isDeleting = deletingId === entry.id;
            return (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium text-foreground">
                      {entry.display_name}
                    </p>
                    {isDocBacked && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        <FileText className="h-3 w-3" />
                        From document
                      </span>
                    )}
                    {isPlaidBacked && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/8 px-2 py-0.5 text-xs font-medium text-primary">
                        <Landmark className="h-3 w-3" />
                        {entry.institution_name
                          ? `From ${entry.institution_name}`
                          : "From bank"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatEntryValue(entry)}
                  </p>
                </div>
                {isEditable && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Edit ${entry.display_name}`}
                      onClick={() => setFormState({ mode: "edit", entry })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Delete ${entry.display_name}`}
                      disabled={isDeleting}
                      onClick={() => void handleDelete(entry.id)}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {formState.mode === "closed" ? (
        <div className="flex flex-wrap items-center gap-2">
          {verificationActions ? (
            <InputVerificationCTAs
              onConnectBank={verificationActions.onConnectBank}
              onUploadDocs={verificationActions.onUploadDocs}
              variant="compact"
              hierarchy="verification-strength"
              bankComingSoon={bankComingSoon}
            />
          ) : null}
          <Button
            variant={verificationActions ? "ghost" : "outline"}
            size="sm"
            onClick={openAddForm}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {addLabel}
          </Button>
        </div>
      ) : (
        <ManualEntryForm
          kind={kind}
          initial={formState.mode === "edit" ? formState.entry : undefined}
          isSubmitting={isSubmitting}
          error={submitError}
          onSubmit={(req) => void handleSubmit(req)}
          onCancel={closeForm}
        />
      )}
    </div>
  );
}
